import React from 'react';
import { Hammer, Volume2, Info, Compass, ShieldCheck } from 'lucide-react';
import { ArchivalImageCard } from './ArchivalImageCard';
import { useVoiceReader } from '../context/VoiceReaderContext';

export const GilesSection: React.FC = () => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-16 text-[#2c221e] font-sans">
      {/* Chapter Header Banner */}
      <header className="bg-[#f3ede0] p-6 sm:p-10 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#e8dec9] text-[#7a5b3a] text-xs font-serif font-bold uppercase tracking-wider border border-[#d8ceb9]">
            <Hammer className="w-3.5 h-3.5 text-[#7a5b3a]" />
            <span>Somerset Pioneers & Agricultural Contractors</span>
          </div>

          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to The Giles Family Chapter"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight">
          THE GILES FAMILY CAME FROM SOMERSET
        </h1>
        <p className="font-serif text-base sm:text-lg text-[#7a5b3a] italic leading-relaxed">
          Pioneering agricultural machinery, contract ploughing, and traction threshing on the Canterbury Plains.
        </p>
      </header>

      {/* Main Archival Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base leading-relaxed text-[#2c221e]">
        
        {/* Section: Somerset Origins */}
        <section className="space-y-6">
          <p>
            The name Launcelot Giles appears for several generations in the Somerset area around Castle Cary before Benjamin Giles of Shepton Mallett married Mary Taylor of Ansford on 18 April 1809 at Batcombe. It was their son Launcelot Giles who would leave the deep family ties to Somerset and emigrate to New Zealand.
          </p>

          <p>
            Launcelot Giles was born in 1818<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-1">1</a></sup> to Benjamin and Mary Giles in the small town of Evercreech in south Somerset<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-2">2</a></sup>. His wife, Rachel (daughter of Edward and Jane Clothier), was born 8 km away in 1821 at the historic small market town of Castle Cary. They married 3 July 1837 at Marston Bigot (Somerset) and they farmed on the gentle countryside of Somerset.
          </p>

          {/* Image 165 */}
          <ArchivalImageCard
            src="public/images/165 Launcelot Giles born 1818.jpg"
            alt="Launcelot Giles born 1818"
            caption="Launcelot Giles born 1818"
            filenameHint="165 Launcelot Giles born 1818.jpg"
          />

          <p>
            Farming in the mid-1850s had benefitted from better breeding of livestock, new systems of cropping and the invention of seed drills. Clearing of woodlands, reclamation of land in high and low-lying country, and higher yielding crops like wheat and barley replacing rye, and an increase in arable and fodder crops in rotation. All of these meant that the UK was able to feed a rapidly growing population. There was also the introduction of fertilizers through nitrogen in the soils, by spreading animal manure and planting legumes - peas, beans, red and white clover. Launcelot Giles would have been a skilled farmer when he decided to emigrate to New Zealand.
          </p>

          {/* Image 166 (Figure with Caption) */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/166 Worlidge seed drill.gif"
              alt="The Worlidge seed drill from the 1830s"
              caption="The Worlidge seed drill from the 1830s"
              filenameHint="166 Worlidge seed drill.gif"
            />
          </figure>

          <p>
            Launcelot and Rachel (1821-1879) started their family in Somerset, where generations of their families had lived and farmed.
          </p>

          {/* List of Children */}
          <ul className="space-y-3 pl-2 sm:pl-4 border-l-2 border-[#d8ceb9] my-4 text-[#2c221e]">
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Mary Jane</strong> (born at Wincanton 1838-1910)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Lot</strong> (christened Lancelot) was born on 16 January 1840 at Wincanton, Somerset, a very old town on the River Cole and a station on the Somerset and Dorset Railway.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Thomas</strong> (born at Wincanton 1842-1918)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Samuel Clothier</strong> (born at Ansford 1844 - died at Waimate 1918)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Elizabeth Ann</strong> (born at Ansford 1845 - died at Kaiapoi 1919)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Benjamin</strong> (born at Ansford 1848-died at Omihi 1913)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Eliza Jane</strong> (born at Ansford 1850-died in Canterbury 1933)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Martha</strong> (born at Ansford 1853 - died at Kaiapoi 1922)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Ada Augusta Oriental</strong> (born at sea on the "Oriental", at Latitude 23 degrees north, 27 November 1855 died at Clarkville 1939) married Charles Holland (1856-1895) whose brother Henry (1859-1944) was a farmer and Mayor of Christchurch 1912-14 and Reform Party MP for Christchurch North 1925-35. Henry was the father of Sir Sydney George Holland (1893-1961) the 25th Prime Minister of New Zealand (13/12/1949 20/9/1957) and influential in the creation and consolidation of the National Party.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-3">3</a></sup></span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Edward Clothier</strong> (born and died at Clarkville 1857-1929)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>John</strong> (born and died at Clarkville 1859-1909)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Obediah</strong> (born and died at Clarkville 1862-1938)</span>
            </li>
          </ul>

          <p>
            The family left Gravesend on 4 November 1855, on the Oriental, arriving in Lyttelton on 12 April 1856. After a few months in Christchurch they moved to the Clarkville area, north of the Waimakariri River, and named their property Somerset Farm. Launcelot was experienced with farm machinery and was one of the first to import a threshing machine. Over time he imported many modern machines to meet the demand from the arable cropping on the Canterbury Plains.
          </p>

          <p>
            Launcelot's son Edward became a contractor as well. By the time he was seven he was learning to drive bullocks and manage the bullock wagons, but in one accident when he was 11, he was so badly hurt one leg was amputated. That did not slow him down for long and by the time he was 13 he was able to help his father with the threshing machine. When he was 22 he bought his own land at Clarkville and started his own threshing and traction engine business.
          </p>

          {/* Image 167 */}
          <ArchivalImageCard
            src="public/images/167 Threshing Machine.jpg"
            alt="Threshing Machine"
            caption="Threshing Machine"
            filenameHint="167 Threshing Machine.jpg"
          />

          <p>
            Rachel, who arrived with her family on the Oriental in 1856, died 16 April 1879. On 31 December 1879 Launcelot, aged 61, married 26-year-old Phebe Moore, (1853-1928) and they had four children.
          </p>

          {/* Children with Phebe Moore */}
          <ul className="space-y-2.5 pl-2 sm:pl-4 border-l-2 border-[#d8ceb9] my-4 text-[#2c221e]">
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Launce William Ernest Giles</strong> (1884-1962)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Phebe Caroline Mabel McCandish</strong> (1882-1962)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Herkes Herbert Reuben</strong> (1884-1962)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Clara Priscilla Minnie</strong> (Feb 1885 - May 1889)</span>
            </li>
          </ul>
        </section>

        {/* Section: Lot Giles */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            LOT GILES
          </h2>

          {/* Image 168 */}
          <ArchivalImageCard
            src="public/images/168 Lot Giles.jpg"
            alt="Lot Giles"
            caption="Lot Giles"
            filenameHint="168 Lot Giles.jpg"
          />

          <p>
            Lot married Margaret Watt Marshall<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-4">4</a></sup> (born 1841) on 29 November 1864 at Kaiapoi Parish Church. They had ten children before Margaret died 13 October 1882.
          </p>

          {/* Children of Lot & Margaret */}
          <ul className="space-y-3 pl-2 sm:pl-4 border-l-2 border-[#d8ceb9] my-4 text-[#2c221e]">
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Eliza Jane</strong> (1865-1938) married John Frederick Denton, had nine children, and as a widow, later married James Murchison and had one son</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Leith</strong> (1867-1957) married James Ashworth - although she had been earlier engaged to Isaac Ephraim Denton but family lore has it that when he took up his farm in the Hakatamea and did not write to her for a very long time she got annoyed and handed the ring back when she next saw him!</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Albert John</strong> (1869 1942) married Elizabeth Bowman</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Obed Lot</strong> (1870-1931) married Lily Sarah Jackson</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Ada Louise</strong> (1872-1873)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Jessie Constance</strong> (1873 1925) married James Townsend</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Mary Ann</strong> (1874-75)</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Martha Margaret</strong> (born 1876) married George McGregor</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Rebecca</strong> (1878-1948) married and later divorced Bix (Albert Edward) Denton, and was known within the family as "The Duchess" - for her son Clarrie's wedding to Gretchen (Greta) Dierck the newspaper reported: "The mother of the groom wore a very smart oyster grey velour costume, beautifully braided with black and a Parisian lace hat and carried a bouquet of scarlet anenomes."</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
              <span><strong>Herbert</strong> (1882-82)</span>
            </li>
          </ul>

          <p>
            Eight months after Margaret died, Lot (43) married Sarah Clothier<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-5">5</a></sup>, widowed in June 1865, on 20 June 1883 at the house of Launcelot and Rachel Giles' house.
          </p>

          <p>
            THE OPPORTUNITY for introducing agricultural new machines was huge on the Canterbury Plains. The double furrow plough sped up the conversion of arable crops, water races allowed stock to be spread out further, steam threshing machines and a rang of new machines enabled large tracts of wheat in particular to be harvested, and from the 1870's - 90's there was a wheat boom. The rapid development of the railway system, labour gangs of men with bullocks and ploughs, entrepreneurial contractors such as the Giles family - all played their part in the development of Canterbury farming and its contribution to the national wealth.
          </p>

          {/* Image 169 (Figure with Caption) */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/169 Double Furrow Plow.jpg"
              alt="Double Furrow Plow"
              caption="The double furrow plough had immediate impact"
              filenameHint="169 Double Furrow Plow.jpg"
            />
          </figure>

          {/* Image 170 */}
          <ArchivalImageCard
            src="public/images/170 Contract Plough in North Canterbury.jpg"
            alt="Contract Plough in North Canterbury"
            caption="Contract Plough in North Canterbury"
            filenameHint="170 Contract Plough in North Canterbury.jpg"
          />
        </section>

        {/* Footnotes Section */}
        <section className="pt-8 border-t-2 border-[#d8ceb9] space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl">
          <div className="flex items-center gap-2 text-[#7a5b3a] font-serif font-bold text-sm uppercase tracking-wider">
            <Info className="w-4 h-4" />
            <span>Footnotes</span>
          </div>

          <ol className="space-y-2 text-xs sm:text-sm text-[#4a3525] leading-relaxed list-decimal list-inside pl-1">
            <li id="fn-1" className="scroll-mt-24">
              The Cyclopedia of New Zealand, Vol 3, Canterbury Provincial District 1903 pp 443-445
            </li>
            <li id="fn-2" className="scroll-mt-24">
              The bells of Evercreech's St Peter's Church are played every Sunday morning on the BBC.
            </li>
            <li id="fn-3" className="scroll-mt-24">
              Louisa Alice Denton would marry Edward Clothier (in 1875) whose parents came from Castle Cary
            </li>
            <li id="fn-4" className="scroll-mt-24">
              Margaret's parents were David Watt Marshall (1804-1888), married 13/7/1827 at Gorbals, Lanark, Scotland, Jane Telfer, born 1806
            </li>
            <li id="fn-5" className="scroll-mt-24">
              Sarah was the daughter of Benjamin and Anne Seymour, nee Grant
            </li>
          </ol>
        </section>

      </article>
    </div>
  );
};
